/**
 * React Query Hooks for lovabletsaward Data Access
 * Connector track for Epic 6: BAMv3 Interface Integration
 * Provides optimized data fetching with caching, background updates, and error handling
 */

import {
  useQuery,
  useQueryClient,
  type UseQueryOptions,
  type UseQueryResult,
  type QueryKey
} from '@tanstack/react-query';
import {
  getProgramsPriorityList,
  validateConnection,
  searchProgramsAdvanced,
  getProgramsExtended,
  getQualityMetrics,
  getSystemHealth,
  getEnrichmentStatus,
  getProgramDetails,
  ping,
  isRPCError,
  getErrorMessage
} from '../services/rpc';
import type { 
  ProgramPriorityItem, 
  ConnectionStatus, 
  SearchFilters, 
  ProgramSearchResults,
  QualityMetrics,
  SystemHealth,
  EnrichmentStatus,
  ProgramDetails
} from '../types/rpc-contracts';
import { RPC_QUERY_KEYS, RPC_CACHE_CONFIG } from '../types/rpc-contracts';

/**
 * Hook for fetching priority programs list
 * Used by DataQualityHub for displaying top priority programs
 */
type ProgramsPriorityKey = typeof RPC_QUERY_KEYS.programs.priority;
type ProgramsSearchKey = readonly ['lovabletsaward', 'programs', 'search', SearchFilters];
type ProgramsExtendedKey = readonly ['lovabletsaward', 'programs', 'extended', SearchFilters];
type SystemConnectionKey = typeof RPC_QUERY_KEYS.system.connection;
type SystemMetricsKey = typeof RPC_QUERY_KEYS.system.metrics;
type SystemHealthKey = typeof RPC_QUERY_KEYS.system.health;
type EnrichmentStatusKey = readonly ['lovabletsaward', 'enrichment', 'status', string | undefined];

type QueryOptions<TData, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<TData, Error, TData, TQueryKey>,
  'queryKey' | 'queryFn'
>;

export function useProgramsPriority(
  options?: QueryOptions<ProgramPriorityItem[], ProgramsPriorityKey>
): UseQueryResult<ProgramPriorityItem[], Error> {
  return useQuery<ProgramPriorityItem[], Error, ProgramPriorityItem[], ProgramsPriorityKey>({
    queryKey: RPC_QUERY_KEYS.programs.priority,
    queryFn: async (): Promise<ProgramPriorityItem[]> => {
      return await getProgramsPriorityList();
    },
    staleTime: RPC_CACHE_CONFIG.staleTime.priority,
    gcTime: RPC_CACHE_CONFIG.cacheTime.priority,
    retry: (failureCount: number, error: unknown) => {
      if (isRPCError(error) && error.rpcError.code.startsWith('INVALID_')) {
        return false;
      }
      return failureCount < 3;
    },
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options
  });
}

/**
 * Hook for checking system connection status
 * Used for system health monitoring and diagnostics
 */
export function useConnectionStatus(
  options?: QueryOptions<ConnectionStatus, SystemConnectionKey>
): UseQueryResult<ConnectionStatus, Error> {
  return useQuery<ConnectionStatus, Error, ConnectionStatus, SystemConnectionKey>({
    queryKey: RPC_QUERY_KEYS.system.connection,
    queryFn: async (): Promise<ConnectionStatus> => {
      return await validateConnection();
    },
    staleTime: RPC_CACHE_CONFIG.staleTime.connection,
    gcTime: RPC_CACHE_CONFIG.cacheTime.connection,
    refetchInterval: RPC_CACHE_CONFIG.refetchInterval.connection,
    refetchIntervalInBackground: false,
    retry: 2, // Only retry twice for connection checks
    retryDelay: 2000, // Fixed delay for connection retries
    ...options
  });
}

/**
 * Hook for advanced program search
 * Used by ProgramSearch component and other search interfaces
 */
export function useProgramsSearch(
  filters: SearchFilters,
  options?: QueryOptions<
    ProgramSearchResults,
    ProgramsSearchKey
  >
): UseQueryResult<ProgramSearchResults, Error> {
  return useQuery<ProgramSearchResults, Error, ProgramSearchResults, ProgramsSearchKey>({
    queryKey: RPC_QUERY_KEYS.programs.search(filters),
    queryFn: async (): Promise<ProgramSearchResults> => {
      return await searchProgramsAdvanced(filters);
    },
    enabled: Object.keys(filters).length > 0, // Only run if filters are provided
    staleTime: RPC_CACHE_CONFIG.staleTime.search,
    gcTime: RPC_CACHE_CONFIG.cacheTime.search,
    retry: (failureCount: number, error: unknown) => {
      // Don't retry on invalid filters
      if (isRPCError(error) && error.rpcError.code.startsWith('INVALID_')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1500,
    ...options
  });
}

/**
 * Hook for extended programs list (pagination/sorting without requiring text filters)
 */
export function useProgramsExtended(
  filters: SearchFilters,
  options?: QueryOptions<
    ProgramSearchResults,
    ProgramsExtendedKey
  >
): UseQueryResult<ProgramSearchResults, Error> {
  return useQuery<ProgramSearchResults, Error, ProgramSearchResults, ProgramsExtendedKey>({
    queryKey: ['lovabletsaward', 'programs', 'extended', filters] as ProgramsExtendedKey,
    queryFn: async (): Promise<ProgramSearchResults> => {
      return await getProgramsExtended(filters);
    },
    enabled: Object.keys(filters).length > 0,
    staleTime: RPC_CACHE_CONFIG.staleTime.search,
    gcTime: RPC_CACHE_CONFIG.cacheTime.search,
    retry: (failureCount: number, error: unknown) => {
      if (isRPCError(error) && error.rpcError.code.startsWith('INVALID_')) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: 1500,
    ...options
  });
}

/**
 * Hook for quality metrics overview
 * Used by DataQualityHub for system quality dashboard
 */
export function useQualityMetrics(
  options?: QueryOptions<QualityMetrics, SystemMetricsKey>
): UseQueryResult<QualityMetrics, Error> {
  return useQuery<QualityMetrics, Error, QualityMetrics, SystemMetricsKey>({
    queryKey: RPC_QUERY_KEYS.system.metrics,
    queryFn: async (): Promise<QualityMetrics> => {
      return await getQualityMetrics();
    },
    staleTime: RPC_CACHE_CONFIG.staleTime.metrics,
    gcTime: RPC_CACHE_CONFIG.cacheTime.metrics,
    retry: 2,
    retryDelay: 3000,
    ...options
  });
}

/**
 * Hook for system health status
 * Used for operational monitoring and status displays
 */
export function useSystemHealth(
  options?: QueryOptions<SystemHealth, SystemHealthKey>
): UseQueryResult<SystemHealth, Error> {
  return useQuery<SystemHealth, Error, SystemHealth, SystemHealthKey>({
    queryKey: RPC_QUERY_KEYS.system.health,
    queryFn: async (): Promise<SystemHealth> => {
      return await getSystemHealth();
    },
    staleTime: RPC_CACHE_CONFIG.staleTime.metrics,
    refetchInterval: RPC_CACHE_CONFIG.refetchInterval.health,
    refetchIntervalInBackground: false,
    retry: 2,
    retryDelay: 2500,
    ...options
  });
}

/**
 * Hook for enrichment status tracking
 * Used for program enrichment progress monitoring
 */
export function useEnrichmentStatus(
  program_id?: string,
  options?: QueryOptions<EnrichmentStatus[], EnrichmentStatusKey>
): UseQueryResult<EnrichmentStatus[], Error> {
  return useQuery<EnrichmentStatus[], Error, EnrichmentStatus[], EnrichmentStatusKey>({
    queryKey: ['lovabletsaward', 'enrichment', 'status', program_id] as EnrichmentStatusKey,
    queryFn: async (): Promise<EnrichmentStatus[]> => {
      return await getEnrichmentStatus(program_id ?? undefined);
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: 2000,
    ...options
  });
}

/**
 * Hook for lightweight connection ping
 * Used for basic connectivity monitoring
 */
export function useConnectionPing(options?: QueryOptions<boolean, ['lovabletsaward', 'connection', 'ping']>) {
  return useQuery<boolean, Error, boolean, ['lovabletsaward', 'connection', 'ping']>({
    queryKey: ['lovabletsaward', 'connection', 'ping'],
    queryFn: async () => {
      return await ping();
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 30 * 1000, // Ping every 30 seconds
    refetchIntervalInBackground: true,
    retry: 1,
    retryDelay: 1000,
    ...options
  });
}

/**
 * Custom hook utilities for cache management
 */
export function useProgramDetails(
  program_id: string,
  options?: QueryOptions<ProgramDetails, ['lovabletsaward', 'programs', 'details', string]>
): UseQueryResult<ProgramDetails, Error> {
  return useQuery<ProgramDetails, Error, ProgramDetails, ['lovabletsaward', 'programs', 'details', string]>({
    queryKey: ['lovabletsaward', 'programs', 'details', program_id],
    queryFn: async () => {
      return await getProgramDetails(program_id);
    },
    enabled: !!program_id,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    retryDelay: 1500,
    ...options
  });
}

export const useLovabletsawardCache = () => {
  const queryClient = useQueryClient();

  return {
    /**
     * Invalidate priority programs cache
     * Call this when programs data changes
     */
    invalidatePriorityPrograms: () => {
      return queryClient.invalidateQueries({ queryKey: RPC_QUERY_KEYS.programs.priority });
    },

    /**
     * Invalidate all search results
     * Call this when search results may be stale
     */
    invalidateSearchResults: () => {
      return queryClient.invalidateQueries({ 
        queryKey: ['lovabletsaward', 'programs', 'search'] 
      });
    },

    /**
     * Invalidate system status data
     * Call this when system status changes
     */
    invalidateSystemStatus: () => {
      return queryClient.invalidateQueries({ 
        queryKey: ['lovabletsaward', 'system'] 
      });
    },

    /**
     * Pre-fetch priority programs
     * Useful for warming cache
     */
    prefetchPriorityPrograms: () => {
      return queryClient.prefetchQuery({
        queryKey: RPC_QUERY_KEYS.programs.priority,
        queryFn: () => getProgramsPriorityList(),
        staleTime: RPC_CACHE_CONFIG.staleTime.priority,
      });
    },

    /**
     * Get cached connection status
     * Returns cached data without triggering a fetch
     */
    getCachedConnectionStatus: (): ConnectionStatus | undefined => {
      return queryClient.getQueryData(RPC_QUERY_KEYS.system.connection);
    },

    /**
     * Set connection status data
     * Manually update cache (useful for optimistic updates)
     */
    setConnectionStatus: (data: ConnectionStatus) => {
      queryClient.setQueryData(RPC_QUERY_KEYS.system.connection, data);
    },

    /**
     * Clear all lovabletsaward cache
     * Nuclear option for cache issues
     */
    clearAllCache: () => {
      return queryClient.removeQueries({ 
        queryKey: ['lovabletsaward'] 
      });
    }
  };
};

/**
 * Hook for error handling and retry logic
 */
export const useLovabletsawardError = () => {
  return {
    /**
     * Get user-friendly error message
     */
    getErrorMessage,

    /**
     * Check if error is retryable
     */
    isRetryableError: (error: unknown): boolean => {
      if (!isRPCError(error)) return true;
      
      // Don't retry validation errors
      const nonRetryableCodes = [
        'INVALID_LIMIT',
        'INVALID_OFFSET', 
        'INVALID_QUALITY_SCORE',
        'INVALID_YEAR_RANGE',
        'INVALID_RESPONSE_FORMAT'
      ];
      
      return !nonRetryableCodes.includes(error.rpcError.code);
    },

    /**
     * Determine retry delay based on error type
     */
    getRetryDelay: (error: any, attemptIndex: number): number => {
      if (isRPCError(error)) {
        switch (error.rpcError.code) {
          case 'CONNECTION_VALIDATION_FAILED':
            return 2000; // Fixed delay for connection issues
          case 'SEARCH_FAILED':
            return 1500; // Shorter delay for search failures
          default:
            return Math.min(1000 * 2 ** attemptIndex, 30000);
        }
      }
      
      return Math.min(1000 * 2 ** attemptIndex, 30000);
    }
  };
};
